// YERİ: src/app/core/i18n/locale.ts
//
// Uygulamanın DESTEKLEDİĞİ diller — tek kaynak.
//
// Aynı liste iki yerde kullanılıyor: vitrinin dil seçicisi (orada asıl kaynak
// Language TABLOSU, çünkü hangi dilin yayında olduğu operasyonel bir karar) ve
// portalın Görünüm ayarları. Portalda tablo okumuyoruz: portal dili bir içerik
// kararı değil, kullanıcının kendi tercihi — vitrinde İngilizce'den başka dil
// yayında olmasa bile bir kullanıcı panelini Türkçe tarih biçimiyle görmek
// isteyebilir.
//
// ── Bugün dil seçimi NE YAPIYOR ────────────────────────────────────────────
//  1. <html lang>  — ekran okuyucu telaffuzu.
//  2. <html dir>   — Arapça/Farsça'da TÜM düzen sağdan sola döner.
//  3. LOCALE_ID    — tarih, saat, sayı ve para birimi biçimleri (DatePipe,
//                    DecimalPipe, CurrencyPipe). "5 Ağustos 2026" ↔
//                    "August 5, 2026" ↔ "٥ أغسطس ٢٠٢٦".
//
// ── Bugün NE YAPMIYOR ──────────────────────────────────────────────────────
//  Arayüz METİNLERİ hâlâ Türkçe. Bunları çevirmek (Transloco / Batch 10)
//  ayrı ve çok daha büyük bir iş; binlerce sabit string'in çıkarılmasını
//  gerektiriyor ve metinler stabilleşmeden yapılırsa her değişiklik 11 dosyayı
//  bozar. Buradaki altyapı o adımın ÖN KOŞULU: seçilen dil zaten saklanıyor,
//  yön ve biçimler zaten bağlı; Transloco eklendiğinde tek yapılacak sözlüğü
//  bu koda bağlamak.

/** Uygulamanın tanıdığı bir dil. */
export interface AppLocale {
  /** Language tablosundaki kod ve URL öneki ('en', 'en-ca', 'fr'). */
  code: string;
  /** Angular'ın locale kimliği — biçimlendirme verisi bu adla kayıtlı. */
  angularLocale: string;
  /** Yönetim/İngilizce ad. */
  name: string;
  /** Kendi dilindeki ad — seçicide gösterilen metin. */
  nativeName: string;
  /** Sağdan sola yazılan dil. */
  rtl: boolean;
}

export const APP_LOCALES: readonly AppLocale[] = [
  { code: 'en',    angularLocale: 'en',    name: 'English (US)',     nativeName: 'English (US)',     rtl: false },
  { code: 'en-ca', angularLocale: 'en-CA', name: 'English (Canada)', nativeName: 'English (Canada)', rtl: false },
  { code: 'fr',    angularLocale: 'fr',    name: 'French',           nativeName: 'Français',         rtl: false },
  { code: 'de',    angularLocale: 'de',    name: 'German',           nativeName: 'Deutsch',          rtl: false },
  { code: 'it',    angularLocale: 'it',    name: 'Italian',          nativeName: 'Italiano',         rtl: false },
  { code: 'es',    angularLocale: 'es',    name: 'Spanish',          nativeName: 'Español',          rtl: false },
  { code: 'pt',    angularLocale: 'pt',    name: 'Portuguese',       nativeName: 'Português',        rtl: false },
  { code: 'ru',    angularLocale: 'ru',    name: 'Russian',          nativeName: 'Русский',          rtl: false },
  { code: 'tr',    angularLocale: 'tr',    name: 'Turkish',          nativeName: 'Türkçe',           rtl: false },
  { code: 'fa',    angularLocale: 'fa',    name: 'Persian',          nativeName: 'فارسی',            rtl: true  },
  { code: 'ar',    angularLocale: 'ar',    name: 'Arabic',           nativeName: 'العربية',           rtl: true  },
];

/**
 * Portalın varsayılan dili.
 *
 * Arayüz metinleri Türkçe olduğu hâlde varsayılan 'tr' DEĞİL: uygulama Kanada'da
 * çalışıyor, tarih/saat/para biçimlerinin oradaki beklentiye uyması gerekiyor
 * (ör. para birimi CAD, tarih "August 5, 2026"). Dili gerçekten değiştirmek
 * isteyen kullanıcı Görünüm ayarlarından seçiyor.
 */
export const DEFAULT_LOCALE = 'en-ca';

export function findLocale(code: string | null | undefined): AppLocale {
  const normalized = (code ?? '').trim().toLowerCase();
  return APP_LOCALES.find((l) => l.code === normalized)
    ?? APP_LOCALES.find((l) => l.code === DEFAULT_LOCALE)!;
}

/**
 * Angular'ın biçimlendirme verisini yükler ve kaydeder.
 *
 * Yol SABİT yazılmak zorunda (`import('@angular/common/locales/' + code)`
 * ÇALIŞMAZ): esbuild dinamik yolu statik olarak çözemez, chunk üretmez ve
 * çalışma anında modül bulunamaz. Bu yüzden switch — her dal kendi tembel
 * chunk'ını üretir, yalnız seçilen dilin verisi indirilir (~25 KB).
 */
export async function registerAppLocale(code: string): Promise<void> {
  const locale = findLocale(code);

  // 'en' Angular'da yerleşik — ayrıca kaydedilmesine gerek yok.
  if (locale.angularLocale === 'en') return;

  const { registerLocaleData } = await import('@angular/common');

  const data = await loadLocaleData(locale.code);
  if (data) registerLocaleData(data, locale.angularLocale);
}

async function loadLocaleData(code: string): Promise<unknown | null> {
  switch (code) {
    case 'en-ca': return (await import('@angular/common/locales/en-CA')).default;
    case 'fr':    return (await import('@angular/common/locales/fr')).default;
    case 'de':    return (await import('@angular/common/locales/de')).default;
    case 'it':    return (await import('@angular/common/locales/it')).default;
    case 'es':    return (await import('@angular/common/locales/es')).default;
    case 'pt':    return (await import('@angular/common/locales/pt')).default;
    case 'ru':    return (await import('@angular/common/locales/ru')).default;
    case 'tr':    return (await import('@angular/common/locales/tr')).default;
    case 'fa':    return (await import('@angular/common/locales/fa')).default;
    case 'ar':    return (await import('@angular/common/locales/ar')).default;
    default:      return null;
  }
}
